if (!customElements.get('featured-categories-slider')) {
  class FeaturedCategoriesSlider extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.featured-categories__slider-viewport');
      this.track = this.querySelector('.featured-categories__slider-track');
      this.prevBtn = this.querySelector('.featured-categories__slider-btn--prev');
      this.nextBtn = this.querySelector('.featured-categories__slider-btn--next');

      const section = this.closest('.featured-categories');
      this.activeLabel = section && section.querySelector('.featured-categories__active-label-text');
      this.activeLabelLink = section && section.querySelector('.featured-categories__active-label-link');

      this.originalSlides = [...this.track.children];
      this.totalSlides = this.originalSlides.length;
      this.currentIndex = 0;
      this.cloneCount = 0;
      this.isAnimating = false;
      this.isDragging = false;
      this.startX = 0;
      this.dragDelta = 0;
      this._slideWidth = 0;
      this._gap = 0;
      this._initialized = false;

      this.setupClones();
      this.bindEvents();

      this.resizeObserver = new ResizeObserver(entries => {
        const vw = entries[0].contentRect.width;
        if (!vw) return;
        this._applyLayout();
        this.setPosition(false);
        this.updateActive();
      });

      this.resizeObserver.observe(this.viewport);
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      window.removeEventListener('mousemove', this._onMouseMove);
      window.removeEventListener('mouseup', this._onMouseUp);
    }

    setupClones() {
      this.track.querySelectorAll('[data-clone]').forEach(el => el.remove());

      const startClones = this.originalSlides.slice(-this.totalSlides).map(slide => {
        const clone = slide.cloneNode(true);
        clone.setAttribute('data-clone', 'start');
        clone.setAttribute('aria-hidden', 'true');
        return clone;
      });

      const endClones = this.originalSlides.slice(0, this.totalSlides).map(slide => {
        const clone = slide.cloneNode(true);
        clone.setAttribute('data-clone', 'end');
        clone.setAttribute('aria-hidden', 'true');
        return clone;
      });

      [...startClones].reverse().forEach(clone => {
        this.track.insertBefore(clone, this.track.firstChild);
      });
      endClones.forEach(clone => this.track.appendChild(clone));

      this.cloneCount = this.totalSlides;
    }

    get columnsCount() {
      return window.innerWidth >= 750
        ? parseInt(this.dataset.columnsDesktop) || 7
        : parseInt(this.dataset.columnsMobile) || 3;
    }

    _applyLayout() {
      const computedGap = parseFloat(getComputedStyle(this.track).columnGap);
      this._gap = isNaN(computedGap) ? 0 : computedGap;

      const N = this.columnsCount;
      const viewportWidth = this.viewport.offsetWidth;
      this._slideWidth = (viewportWidth - (N - 1) * this._gap) / N;

      const itemPadding = this._gap / 2;
      this.track.querySelectorAll('.featured-categories__item').forEach(item => {
        item.style.width = `${this._slideWidth}px`;
        item.style.paddingLeft = `${itemPadding}px`;
        item.style.paddingRight = `${itemPadding}px`;
      });

      // Card width inside padding = slideWidth - gap. Cap scale so overflow stays within padding zone.
      const cardWidth = this._slideWidth - this._gap;
      const isMobileLayout = window.innerWidth < 750;
      const maxScale = isMobileLayout
        ? Math.min(1.3, 1 + (this._gap * 2.5) / cardWidth)
        : Math.min(1.15, 1 + (this._gap * 0.9) / cardWidth);
      this._maxScale = maxScale;

      // Vertical padding on viewport = scale overflow height so overflow:hidden doesn't clip the card.
      const scaleOverflow = Math.ceil((maxScale - 1) / 2 * cardWidth);
      this.viewport.style.paddingTop = `${scaleOverflow}px`;
      this.viewport.style.paddingBottom = `${scaleOverflow}px`;
    }

    getTranslateX(index) {
      const viewportWidth = this.viewport.offsetWidth;
      const centerOffset = (viewportWidth - this._slideWidth) / 2;
      return -(this.cloneCount + index) * (this._slideWidth + this._gap) + centerOffset;
    }

    setPosition(animate) {
      const x = this.getTranslateX(this.currentIndex);
      if (animate) {
        this.track.style.transition = 'transform 0.45s ease';
        this.track.getBoundingClientRect();
        this.track.style.transform = `translateX(${x}px)`;
      } else {
        this.track.style.transition = 'none';
        this.track.style.transform = `translateX(${x}px)`;
      }
    }

    _silentJump(index) {
      this.currentIndex = index;
      this.track.style.transition = 'none';
      this.track.getBoundingClientRect();
      this.track.style.transform = `translateX(${this.getTranslateX(index)}px)`;
    }

    updateActive() {
      const normalizedIndex = ((this.currentIndex % this.totalSlides) + this.totalSlides) % this.totalSlides;

      [...this.track.children].forEach(slide => {
        const slideIndex = parseInt(slide.dataset.index);
        const isActive = slideIndex === normalizedIndex;

        let dist = Math.abs(slideIndex - normalizedIndex);
        dist = Math.min(dist, this.totalSlides - dist);

        slide.classList.toggle('featured-categories__item--active', isActive);
        const isMobile = window.innerWidth < 750;
        const activeScale = this._maxScale || 1.1;
        const inactiveScale = isMobile
          ? activeScale * Math.max(0.48, 0.6 - (dist - 1) * 0.12)
          : Math.max(0.82, 1 - dist * 0.06);
        slide.style.setProperty('--card-scale', isActive ? activeScale : inactiveScale);
      });

      const activeSlide = this.originalSlides[normalizedIndex];
      if (this.activeLabel) {
        this.activeLabel.textContent = activeSlide?.dataset.label || '';
      }
      if (this.activeLabelLink) {
        const link = activeSlide?.querySelector('a');
        this.activeLabelLink.href = link?.getAttribute('href') || '#';
      }
    }

    next() {
      if (this.isAnimating) return;
      this.isAnimating = true;
      this.currentIndex++;
      this.updateActive();
      this.setPosition(true);
      this.track.addEventListener('transitionend', () => {
        this.isAnimating = false;
        if (this.currentIndex >= this.totalSlides) this._silentJump(0);
      }, { once: true });
    }

    prev() {
      if (this.isAnimating) return;
      this.isAnimating = true;
      this.currentIndex--;
      this.updateActive();
      this.setPosition(true);
      this.track.addEventListener('transitionend', () => {
        this.isAnimating = false;
        if (this.currentIndex < 0) this._silentJump(this.totalSlides - 1);
      }, { once: true });
    }

    bindEvents() {
      this.prevBtn.addEventListener('click', () => this.prev());
      this.nextBtn.addEventListener('click', () => this.next());

      this.track.addEventListener('touchstart', e => {
        this.dragStart(e.touches[0].clientX, e.touches[0].clientY);
      }, { passive: true });

      this.track.addEventListener('touchmove', e => {
        this.dragMoveTouch(e);
      }, { passive: false });

      this.track.addEventListener('touchend', () => this.dragEnd());

      this._onMouseMove = e => this.dragMove(e.clientX);
      this._onMouseUp = () => this.dragEnd();

      this.track.addEventListener('mousedown', e => {
        e.preventDefault();
        this.dragStart(e.clientX, 0);
      });
      window.addEventListener('mousemove', this._onMouseMove);
      window.addEventListener('mouseup', this._onMouseUp);
    }

    dragStart(x, y) {
      if (this.isAnimating) return;
      this.isDragging = false;
      this._touchLocked = false;
      this.startX = x;
      this.startY = y;
      this.dragDelta = 0;
      this.track.style.transition = 'none';
    }

    dragMoveTouch(e) {
      const touch = e.touches[0];
      const dx = touch.clientX - this.startX;
      const dy = touch.clientY - this.startY;

      if (!this.isDragging && !this._touchLocked) {
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
        if (Math.abs(dy) > Math.abs(dx)) {
          this._touchLocked = true;
          return;
        }
        this.isDragging = true;
      }

      if (!this.isDragging) return;
      e.preventDefault();
      this.dragMove(touch.clientX);
    }

    dragMove(x) {
      if (!this.isDragging) return;
      this.dragDelta = x - this.startX;
      const base = this.getTranslateX(this.currentIndex);
      this.track.style.transform = `translateX(${base + this.dragDelta}px)`;
    }

    dragEnd() {
      if (!this.isDragging) return;
      this.isDragging = false;
      const threshold = Math.max(50, this._slideWidth * 0.2);
      if (this.dragDelta < -threshold) {
        this.next();
      } else if (this.dragDelta > threshold) {
        this.prev();
      } else {
        this.setPosition(true);
      }
    }
  }

  customElements.define('featured-categories-slider', FeaturedCategoriesSlider);
}
